"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { ProductItem, getProducts, placeBid, getUserProducts, supabase } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Package, Search, Loader2, DollarSign, Repeat, Gavel, Clock, User, Car, HardDrive, Info, LogIn, Tag, Truck, MessageSquare, Tree } from '@/components/icons';
import { useToast } from '@/components/ui/use-toast';
import { getStoreById } from '@/lib/supabase/stores';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Link, useNavigate } from 'react-router-dom';
import useDebounce from '@/hooks/use-debounce';
import { useSession } from '@/context/SessionContext';
import { formatDistanceToNow, isPast, format } from 'date-fns';
import { getAvatarInitials, cn } from '@/lib/utils';
import { es } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import {
  Dialog, // Keep Dialog for other uses if any, but not for bidding
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import FlipCounter from '@/components/FlipCounter';
import { RealtimeChannel, Session } from '@supabase/supabase-js'; // Import Session
import CountdownTimer from './CountdownTimer';
import { translateCondition, formatCurrency, translateVehicleDetail } from '@/lib/utils'; // Import new utilities
import ProductActivityFeed from './ProductActivityFeed';
import { ProductSidebar } from './ProductSidebar';
import { getProductById } from '@/lib/supabase';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { incrementProductViewCount } from '@/lib/supabase/products';
import { Eye } from 'lucide-react';

interface ProductCardProps {
  product: ProductItem;
  currentUserId: string | undefined;
  session: Session | null; // Add session prop here
  onProductUpdate: (updatedProduct: ProductItem) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product: initialProduct, currentUserId, session, onProductUpdate }) => {
  const [product, setProduct] = useState<ProductItem>(initialProduct);
  const [auctionEndedByTimer, setAuctionEndedByTimer] = useState(false); // New state
  const sellerUsername = product.profiles?.username || 'Usuario Desconocido';
  const sellerAvatarUrl = product.profiles?.avatar_url;
  const [storeInfo, setStoreInfo] = useState<any | null>(null);
  const [storeLoading, setStoreLoading] = useState(false);
  const [contentLoaded, setContentLoaded] = useState<boolean>(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const context = useContentSidebar();
  const { setProductSidebarOpen, setSelectedProduct, productSidebarOpen, selectedProduct, setShowProductClickOverlay, showProductClickOverlay } = context;
  const productRef = React.useRef(product);

  useEffect(() => {
  }, [setSelectedProduct, setProductSidebarOpen]);

  useEffect(() => {
    productRef.current = product;
  }, [product]);

  // Detect product ownership from current user id instead of relying on route
  const isProductOwner = currentUserId === product.seller_id;
  const isMyProductsView = isProductOwner;

  // Realtime subscription for this specific product
  useEffect(() => {
    if (!product.id) {
      return;
    }

    const channel: RealtimeChannel = supabase
      .channel(`product_updates_${product.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'products',
          filter: `id=eq.${product.id}`
        },
        (payload) => {
          const updatedProductData = payload.new as ProductItem;
          // Merge with previous state to preserve joins like 'profiles' which are not in the message payload
          setProduct(prev => {
            const merged = { ...prev, ...updatedProductData };
            onProductUpdate(merged); // Notify parent with merged data
            return merged;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [product.id, onProductUpdate]);

  // Load store info if product is associated to a store
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!product?.store_id) {
          if (mounted) {
            setStoreInfo(null);
            setStoreLoading(false);
          }
          return;
        }
        if (mounted) setStoreLoading(true);
        const s = await getStoreById(product.store_id as string);
        if (mounted) setStoreInfo(s || null);
      } catch (e) {
        console.warn('Failed to load store for product header', e);
        if (mounted) setStoreInfo(null);
      }
    })();
    return () => { mounted = false; };
  }, [product.store_id]);

  React.useEffect(() => {
    // clear loading once storeInfo is set (either object or null)
    if (storeLoading && typeof storeInfo !== 'undefined') setStoreLoading(false);
  }, [storeInfo]);

  // contentLoaded effect moved below after displayImageUrl is defined

  // Update internal product state if initialProduct prop changes (e.g., parent re-fetches the list)
  useEffect(() => {
    setProduct(initialProduct);
    setAuctionEndedByTimer(false); // Reset timer flag when product changes
  }, [initialProduct]);

  // Incrementa el contador de vistas al abrir el detalle
  const handleOpenProduct = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Si el sidebar ya está abierto con este mismo producto, solo asegurar que esté visible
    if (productSidebarOpen && selectedProduct?.id === product.id) {
      
      // Asegurar que el sidebar esté abierto (por si acaso)
      setProductSidebarOpen(true);
      
      // Hacer scroll al top del sidebar - buscar el viewport dentro del ScrollArea de Radix
      setTimeout(() => {
        const scrollArea = document.querySelector('[data-product-sidebar-scroll]');
        const viewport = scrollArea?.querySelector('[data-radix-scroll-area-viewport]');
        if (viewport) {
          viewport.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 50);
      
      // Feedback visual opcional: pequeño bounce
      const card = e?.currentTarget;
      if (card instanceof HTMLElement) {
        card.style.animation = 'none';
        setTimeout(() => {
          card.style.animation = 'bounce 0.3s ease-in-out';
        }, 10);
        setTimeout(() => {
          card.style.animation = '';
        }, 310);
      }
      
      return;
    }
    
    // Mostrar el contenido de la sidebar con datos parciales
    setShowProductClickOverlay(true);
    setSelectedProduct(product);
    setProductSidebarOpen(true);
    
    // Luego cargar el contenido completo en background
    try {
      incrementProductViewCount(product.id).catch(e => console.error('[ProductCard] view count error', e));
      
      const fullProduct = await getProductById(product.id);
      
      if (fullProduct) {
        setSelectedProduct(fullProduct);
      } else {
        console.error('[ProductCard] Could not fetch full product data - keeping partial data');
      }
    } catch (err) {
      console.error('[ProductCard] Error loading full product:', err);
      // Keep the partial product data already shown
    }
  };

  // Combine the auction status from product data and the timer's internal signal
  const isAuctionActive = product.is_auction && product.auction_end_time && !isPast(new Date(product.auction_end_time)) && !auctionEndedByTimer;
  const isAuctionEnded = product.is_auction && (isPast(new Date(product.auction_end_time || 0)) || auctionEndedByTimer);
  const displayImageUrl = product.main_image_url || product.cover_url || (product.image_urls && product.image_urls.length > 0 ? product.image_urls[0] : null);

  // Manage contentLoaded state based on image availability
  React.useEffect(() => {
    if (displayImageUrl) {
      setContentLoaded(false);
    } else {
      setContentLoaded(true);
    }
  }, [displayImageUrl, product.id]);

  // Compute minimum rental option (if any) to display a concise 'Alquiler disponible' row
  let minRentalOption: any = null;
  if (product.rental_options && Array.isArray(product.rental_options) && product.rental_options.length > 0) {
    const validOpts = product.rental_options.filter((o: any) => o && typeof o.price === 'number' && !isNaN(o.price));
    if (validOpts.length > 0) {
      minRentalOption = validOpts.reduce((min: any, cur: any) => (cur.price < min.price ? cur : min), validOpts[0]);
    }
  }

  // Determine whether to show the condition tag.
  const conditionLabel = product.condition ? translateCondition(product.condition) : null;
  // Always show the condition label (including "Nuevo").
  const showCondition = !!conditionLabel;
  // Only show tƒhe whole tags container if there's at least one tag to display
  const showTags = showCondition || !!product.free_shipping;

  return (
      <>
      {/* Overlay seteado por click en card */}
      {showProductClickOverlay && (
        <div className="fixed inset-0 bg-black/20 z-[9996] transition-opacity duration-200 pointer-events-none" />
      )}
      
      <Card className="flex flex-col md:flex-row-reverse overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 relative">
        {/* Imagen arriba en mobile, derecha en desktop - con soporte para múltiples imágenes */}
        {displayImageUrl && (
          <div className="w-full aspect-[4/3] md:w-2/5 md:aspect-[4/3] md:flex-shrink-0 bg-muted relative overflow-hidden rounded-t-lg md:rounded-r-lg md:rounded-t-none">
            {(() => {
              const imageUrls = product.image_urls && product.image_urls.length > 0 ? product.image_urls : [displayImageUrl];
              return (
                <Carousel className="w-full h-full flex flex-col">
                  <CarouselContent className="flex-1 h-full m-0">
                    {imageUrls.map((imageUrl, idx) => (
                      <CarouselItem key={idx} className="h-full basis-full">
                        <div
                          className="cursor-pointer w-full h-full relative overflow-hidden"
                          onClick={handleOpenProduct}
                        >
                          <img
                            src={imageUrl}
                            alt={`${product.title} - Imagen ${idx + 1}`}
                            onLoad={() => setContentLoaded(true)}
                            onError={() => setContentLoaded(true)}
                            className="block w-full h-full object-cover object-center rounded-t-lg md:rounded-r-lg md:rounded-t-none"
                            loading={idx === 0 ? "eager" : "lazy"}
                            />
                          </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {imageUrls.length > 1 && (
                    <>
                      <CarouselPrevious className="left-1 md:left-2" />
                      <CarouselNext className="right-1 md:right-2" />
                    </>
                  )}
                </Carousel>
              );
            })()}
            {/* Tags sobre la imagen */}
            {product.product_type !== 'groceries' && showTags && (
              <div className="absolute top-2 right-2 flex flex-col gap-2 items-end max-w-[calc(100%-1rem)] z-10 pointer-events-none">
                {showCondition && (
                  <span title={conditionLabel || ''} className="flex items-center bg-muted/90 backdrop-blur-sm text-muted-foreground px-2 py-1 rounded-full shadow-md">
                    <Tag className="h-3 w-3 mr-2" />
                    <span className="text-xs">{conditionLabel}</span>
                  </span>
                )}
                {product.free_shipping && (
                  <span title="Envío gratis" className="flex items-center bg-success-muted text-success px-2 py-1 rounded-full text-xs shadow-md">
                    <Truck className="h-3 w-3 mr-2" />
                    <span>Envío gratis</span>
                  </span>
                )}
              </div>
            )}
          </div>
        )}
        <div className={cn("p-4 flex flex-col", displayImageUrl ? "md:w-3/5" : "w-full")}>
        <span
          className="font-bold text-lg mb-1 hover:underline block cursor-pointer"
          onClick={handleOpenProduct}
        >
          <h3>{product.title}</h3>
        </span>
        {/* Show content (measurements) from top-level or details */}
        {(() => {
          const contentValue = product.content || (product.details as any)?.content;
          return contentValue ? (
            <p className="text-xs text-muted-foreground mb-1">{contentValue}</p>
          ) : null;
        })()}
        {product.author && product.product_type !== 'book' && (
          <p className="text-sm text-muted-foreground mb-2">por {product.author}</p>
        )}
        {/* Layout: avatar izquierda + info derecha (dos filas) */}
        <div className="flex gap-3 mb-3 h-10">
          {product.store_id ? (
            storeLoading ? (
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-sm bg-muted whitespace-nowrap" />
                <div className="h-4 w-24 bg-muted rounded" />
              </div>
            ) : storeInfo ? (
              <>
                <div className="flex-shrink-0">
                  <Link to={`/${storeInfo.slug || storeInfo.store_slug || storeInfo.id}`}>
                    <Avatar className="h-8 w-8 rounded-sm">
                      <AvatarImage src={(storeInfo as any).avatar_url || undefined} alt={(storeInfo as any).name || 'Tienda'} />
                      <AvatarFallback>{getAvatarInitials((storeInfo as any).name || 'Tienda')}</AvatarFallback>
                    </Avatar>
                  </Link>
                </div>
                <div className="flex flex-col gap-0.5 text-sm text-muted-foreground">
                  <Link to={`/${storeInfo.slug || storeInfo.store_slug || storeInfo.id}`} className="font-semibold hover:underline text-sm">
                    { (storeInfo as any).name || 'Tienda' }
                    {session && isProductOwner && <span className="ml-2 text-xs font-normal">(Es tu producto)</span>}
                  </Link>
                  <span className="text-xs text-muted-foreground">minibid.org/{storeInfo.slug || storeInfo.store_slug || storeInfo.id}</span>
                </div>
              </>
            ) : (
              // store_id set but store not found - fallback to user
              <>
                <div className="flex-shrink-0">
                  <Link to={`/${sellerUsername}`}>
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={sellerAvatarUrl || undefined} alt={sellerUsername} />
                      <AvatarFallback>
                        {sellerUsername.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                </div>
                <div className="flex flex-col gap-0.5 text-sm text-muted-foreground">
                  <Link to={`/${sellerUsername}`} className="font-semibold hover:underline text-sm">
                    @{sellerUsername}
                    {session && isProductOwner && <span className="ml-2 text-xs font-normal">(Es tu producto)</span>}
                  </Link>
                </div>
              </>
            )
          ) : (
            // no store associated - show user immediately
            <>
              <div className="flex-shrink-0">
                <Link to={`/${sellerUsername}`}>
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={sellerAvatarUrl || undefined} alt={sellerUsername} />
                    <AvatarFallback>
                      {sellerUsername.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Link>
              </div>
              <div className="flex flex-col gap-0.5 text-sm text-muted-foreground">
                <Link to={`/${sellerUsername}`} className="font-semibold hover:underline text-sm">
                  @{sellerUsername}
                  {session && isProductOwner && <span className="ml-2 text-xs font-normal">(Es tu producto)</span>}
                </Link>
              </div>
            </>
          )}
        </div>

        <div className="border-t border-primary/30 my-3" />

        {/* Pricing and auction area */}
        <div className="mt-2 space-y-2">
          {/* Compra ya: prefer buy_now_price for auctions, else fixed_sale_price */}
          {(product.buy_now_price != null || product.fixed_sale_price != null) && !isAuctionEnded && (
            <div className="w-full">
              <div className="flex items-center justify-between text-base font-semibold">
                <span className="flex items-center gap-1 text-primary">
                  <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" /> Compra ya
                </span>
                <span className="text-foreground">{formatCurrency(product.buy_now_price ?? product.fixed_sale_price!)}</span>
              </div>
            </div>
          )}

          {/* Show rental pricing row similar to Compra ya when rental options exist */}
          {minRentalOption && (
            <div className="w-full">
              <div className="flex items-center justify-between text-base font-semibold">
                <div className="flex flex-col">
                  <span className="flex items-center gap-1 text-primary">
                      <Tree className="h-3 w-3 sm:h-4 sm:w-4" /> Alquiler:
                  </span>
                  <span className="text-xs text-muted-foreground">desde</span>
                </div>
                <span className="text-foreground">{formatCurrency(minRentalOption.price)}</span>
              </div>
            </div>
          )}

          {/* Oferta más alta (if auction) */}
          {product.is_auction && (
            <div className="flex items-center justify-between text-base font-semibold">
              <span className="flex items-center gap-1 text-primary">
                <Gavel className="h-3 w-3 sm:h-4 sm:w-4" />
                {isAuctionEnded ? 'Subastado a:' : 'Oferta más alta'}
              </span>
              <FlipCounter value={product.current_bid || product.starting_bid || 0} className="text-foreground" />
            </div>
          )}

          {/* Countdown (if auction active) */}
          {isAuctionActive && product.auction_end_time && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4" /> Tiempo restante:
              </span>
              <CountdownTimer
                endTime={product.auction_end_time}
                onAuctionEnd={() => setAuctionEndedByTimer(true)} // Pass callback
              />
            </div>
          )}

          {/* Rental options are displayed as a pricing row below when available (see Compra ya parity) */}
        </div>
      </div>
    </Card>
    </>
  );
};

export default ProductCard;